// apps/client/src/hooks/useLoanContacts.ts
//
// Canonical loan-keyed contact store hook (Part B of the discharges rework).
//
// SSOT: companies/{companyId}/loan_contacts/{loanId} (doc id = loan_id → one doc
// per loan, deterministic). Each entry is tagged with the report (sourcePage) it
// was created on, so a client's contact history follows them across every report.
//
// Phase 4 (Contract, 2026-06): the 4 legacy per-page collections were backfilled
// into the canonical store and dropped from the read path. This hook now reads
// canonical loan_contacts ONLY. Writes go exclusively through the audited
// mutateLoanContact callable (the deprecated whole-array `saveLoanContacts`
// client write was removed). The normalization + merge logic still lives in the
// shared, unit-tested engine `../utils/contacts/loanContactMerge`; public
// types/values are re-exported here so existing consumers import unchanged.

import { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, query, onSnapshot, DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase';
import type { ContactChannel, ContactOutcome, ContactSourcePage } from '../utils/contacts/loanContactMerge';
import {
  CANONICAL_COLLECTION,
  CanonicalContactDoc,
  LoanContactEntry,
  extractTombstoneIds,
  mergeContactsByLoan,
  normalizeCanonicalEntries,
} from '../utils/contacts/loanContactMerge';

// Re-export the public contract so existing imports from this hook keep working.
export type {
  ContactChannel,
  ContactOutcome,
  ContactSourcePage,
  LoanContactEntry,
} from '../utils/contacts/loanContactMerge';
export { CONTACT_CHANNELS, CONTACT_OUTCOMES } from '../utils/contacts/loanContactMerge';

// Editable fields a user supplies for a create/update (server stamps the rest).
interface ContactEntryDraft {
  channels: ContactChannel[];
  date: string | null;
  broker: string;
  outcome: ContactOutcome | null;
  comments: string;
}

// Operation-based input for the audit-grade write path (mutateLoanContact).
export type MutateContactInput =
  | {
      operation: 'create';
      loanId: string;
      entry: ContactEntryDraft & { sourcePage: ContactSourcePage };
      customerName?: string;
      loanBroker?: string;
      /**
       * Client-generated id for the new entry. The server stores the entry under
       * this id and treats a repeated create carrying it as the SAME entry (it
       * returns the original and writes nothing), so a retry or double submit
       * cannot append a duplicate. Must satisfy the callable's `pathSegment`
       * rule: 1 to 256 characters, no "/".
       */
      clientEntryId?: string;
    }
  | { operation: 'update'; loanId: string; entryId: string; entry: ContactEntryDraft }
  | { operation: 'delete'; loanId: string; entryId: string };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLoanContacts(companyId: string | undefined): {
  contactsByLoan: Record<string, LoanContactEntry[]>;
  loading: boolean;
  error: string | null;
  /** Audit-grade write path → mutateLoanContact callable (server-stamped actor). */
  mutateContact: (input: MutateContactInput) => Promise<{ ok: boolean; entryId: string }>;
} {
  // Canonical entries keyed by loanId.
  const [canonicalByLoan, setCanonicalByLoan] = useState<Record<string, LoanContactEntry[]>>({});
  // Deleted entry ids (canonical tombstones).
  const [tombstonedIds, setTombstonedIds] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loading = !!companyId && !loaded;

  useEffect(() => {
    if (!companyId) return;

    setCanonicalByLoan({});
    setTombstonedIds(new Set());
    setLoaded(false);
    setError(null);

    const canonicalQ = query(collection(db, `companies/${companyId}/${CANONICAL_COLLECTION}`));
    const unsubscribe = onSnapshot(
      canonicalQ,
      (snapshot) => {
        const map: Record<string, LoanContactEntry[]> = {};
        const tombstones = new Set<string>();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as DocumentData as CanonicalContactDoc;
          const loanId = data.loanId || docSnap.id;
          if (!loanId) return;
          map[loanId] = normalizeCanonicalEntries(data);
          for (const id of extractTombstoneIds(data)) tombstones.add(id);
        });
        setCanonicalByLoan(map);
        setTombstonedIds(tombstones);
        setLoaded(true);
      },
      (err) => {
        console.error(`[useLoanContacts] Error on ${CANONICAL_COLLECTION}:`, err);
        setError('Failed to load contact history');
        setLoaded(true);
      }
    );

    return () => unsubscribe();
  }, [companyId]);

  // Dedupe by id + suppress tombstones + newest-first. Legacy sources were
  // dropped in Phase 4 (the engine still accepts them; we just no longer feed
  // any), so the legacy map is always empty here.
  const contactsByLoan = useMemo(
    () => mergeContactsByLoan(canonicalByLoan, {}, tombstonedIds),
    [canonicalByLoan, tombstonedIds]
  );

  // Audit-grade write path. Delegates to the mutateLoanContact callable, which
  // server-stamps the actor, runs a per-entry transaction, authorizes the edit,
  // and audits it. The realtime onSnapshot above reflects the result.
  const mutateContact = useCallback(
    async (input: MutateContactInput): Promise<{ ok: boolean; entryId: string }> => {
      if (!companyId) throw new Error('Cannot mutate contact: no companyId');
      const fn = httpsCallable<Record<string, unknown>, { ok: boolean; entryId: string }>(
        functions,
        'mutateLoanContact'
      );
      const res = await fn({ companyId, ...input });
      return res.data;
    },
    [companyId]
  );

  return { contactsByLoan, loading, error, mutateContact };
}
